'use client';

import { useEffect, useRef } from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { useAccount } from 'wagmi';
import {
  pushDiagnosticEvent,
  setWalletSnapshot,
  shortAddress,
} from '@/lib/diagnostic-state';
import { recordBootPhase } from '@/lib/boot-milestones';

/**
 * Headless observer for Privy + wagmi state.
 *
 * Mounted once inside `<PrivyClientProvider>` (so the Privy / wagmi
 * hooks resolve), renders nothing, and writes a snapshot to
 * `diagnostic-state.ts` whenever the relevant wallet state changes.
 * The snapshot is read by `report-client-error.ts` at the moment a
 * client-side error is captured, *not* at the moment of the change
 * — so even if the providers tree falls over by the time the
 * boundary fires, the *last good* snapshot still reaches the
 * server log.
 *
 * Why this is its own component (and not just instrumentation
 * inside `useWallet.ts`):
 *   - `useWallet` is consumed in dozens of components and would
 *     run the snapshot effect multiple times per render — wasteful
 *     and noisy.
 *   - `useWallet` already does heavy work (RPC fetches, query
 *     invalidation). Adding diagnostics there increases the blast
 *     radius if the snapshotter ever throws.
 *   - This component subscribes only to the *raw* Privy / wagmi
 *     state, with no derived data, so it has no dependency on the
 *     hook stack that has been suspected of contributing to the
 *     #185 loop. If we were to instrument `useWallet` directly
 *     and the loop were inside `useWallet`, the snapshotter would
 *     loop with it.
 *
 * Event ring policy:
 *   - `wallet.connected` / `wallet.disconnected` on the
 *     `authenticated` transition.
 *   - `wallet.address-changed` on a non-null → non-null change of
 *     the wagmi address (treated as an account swap, e.g.
 *     MetaMask → Coinbase or one MM account → another).
 *   - `wallet.chain-changed` on a chainId transition.
 *   - `wallet.linked-account-added` when the linked-account count
 *     grows (covers the post-Twitter / post-Farcaster link path).
 *   - `wallet.connector-changed` when the connector id flips.
 *
 * The events go to a 50-entry ring (see `diagnostic-state.ts`) and
 * are included in the next client-error report. They're the
 * *sequence* signal — answers "what happened in the last few
 * hundred ms before the loop?" — which a flat snapshot cannot.
 */
export function WalletDiagnosticSnapshot() {
  const { ready, authenticated, user } = usePrivy();
  const { wallets } = useWallets();
  const wagmiAccount = useAccount();
  const wagmiAddress = wagmiAccount.address ?? null;
  const wagmiChainId = wagmiAccount.chainId ?? null;
  const wagmiConnected = wagmiAccount.isConnected;
  const wagmiConnectorId = wagmiAccount.connector?.id ?? null;
  const wagmiStatus = wagmiAccount.status ?? null;

  // Track the previous values so we can emit transition events,
  // not just snapshot writes. Using refs (not state) avoids a
  // re-render on every Privy/wagmi tick.
  const prev = useRef<{
    authenticated: boolean | null;
    address: string | null;
    chainId: number | null;
    connectorId: string | null;
    linkedCount: number | null;
  }>({
    authenticated: null,
    address: null,
    chainId: null,
    connectorId: null,
    linkedCount: null,
  });

  const privyReadyRecorded = useRef(false);
  useEffect(() => {
    if (!ready || privyReadyRecorded.current) return;
    privyReadyRecorded.current = true;
    recordBootPhase('wallet.privy_ready', {
      authenticated,
    });
  }, [authenticated, ready]);

  useEffect(() => {
    // Build the snapshot from the latest hook values. Everything
    // here is straight read; no async, no fetch, no allocation
    // beyond the partial object.
    const linkedAccounts = (user?.linkedAccounts ?? []) as unknown as Array<{
      type: string;
      walletClientType?: string;
    }>;
    const linkedAccountTypes = Array.from(
      new Set(linkedAccounts.map((a) => a.type ?? 'unknown')),
    );
    const hasEmbeddedWallet = linkedAccounts.some(
      (a) =>
        (a.type === 'wallet' || a.type === 'smart_wallet') &&
        (a.walletClientType === 'privy' || a.walletClientType === 'privy-v2'),
    );
    const walletClientTypes = Array.from(
      new Set(
        wallets
          .map((w) => w.walletClientType)
          .filter((v): v is string => Boolean(v)),
      ),
    );
    const privyUserIdShort = user?.id ? user.id.slice(0, 8) : null;

    setWalletSnapshot({
      privyReady: ready,
      privyAuthenticated: authenticated,
      privyUserIdShort,
      linkedAccountCount: linkedAccounts.length,
      linkedAccountTypes,
      hasEmbeddedWallet,
      wagmiAddressShort: shortAddress(wagmiAddress),
      wagmiChainId,
      wagmiConnected,
      wagmiConnectorId,
      wagmiStatus,
      walletCount: wallets.length,
      walletClientTypes,
    });

    // ----- Transition events (ring buffer) -----
    const p = prev.current;

    if (p.authenticated !== authenticated) {
      pushDiagnosticEvent(
        'wallet',
        authenticated ? 'wallet.connected' : 'wallet.disconnected',
        {
          ready,
          address: shortAddress(wagmiAddress),
          connector: wagmiConnectorId,
          linkedCount: linkedAccounts.length,
        },
      );
      p.authenticated = authenticated;
    }

    if (p.address !== wagmiAddress) {
      // Treat the very first non-null address as `wallet.address-set`
      // and a subsequent change as `wallet.address-changed` so the
      // log reads naturally.
      const event =
        p.address && wagmiAddress
          ? 'wallet.address-changed'
          : wagmiAddress
            ? 'wallet.address-set'
            : 'wallet.address-cleared';
      pushDiagnosticEvent('wallet', event, {
        from: shortAddress(p.address),
        to: shortAddress(wagmiAddress),
      });
      p.address = wagmiAddress;
    }

    if (p.chainId !== wagmiChainId) {
      pushDiagnosticEvent('wallet', 'wallet.chain-changed', {
        from: p.chainId,
        to: wagmiChainId,
      });
      p.chainId = wagmiChainId;
    }

    if (p.connectorId !== wagmiConnectorId) {
      pushDiagnosticEvent('wallet', 'wallet.connector-changed', {
        from: p.connectorId,
        to: wagmiConnectorId,
      });
      p.connectorId = wagmiConnectorId;
    }

    if (
      p.linkedCount !== null &&
      linkedAccounts.length > p.linkedCount
    ) {
      // Find the new type(s) — most often this is the
      // post-Twitter-link or post-Farcaster-link transition that
      // races with `recordVerifications` in `useWallet`.
      pushDiagnosticEvent('wallet', 'wallet.linked-account-added', {
        from: p.linkedCount,
        to: linkedAccounts.length,
        types: linkedAccountTypes,
      });
    }
    p.linkedCount = linkedAccounts.length;
  }, [
    ready,
    authenticated,
    user,
    wallets,
    wagmiAddress,
    wagmiChainId,
    wagmiConnected,
    wagmiConnectorId,
    wagmiStatus,
  ]);

  return null;
}
